// Localização: frontend/src/api/adminAssinaturaApi.js
// API client para endpoints de gerenciamento admin de assinaturas

import api from './api';

/**
 * Lista todas as organizações com suas assinaturas
 * @param {object} filters - Filtros de busca (status_assinatura, tipo_assinatura, status, search)
 * @param {number} page - Página atual
 * @param {number} limit - Limite de resultados por página
 * @returns {Promise} Lista de organizações com assinaturas
 */
export const listarAssinaturas = async (filters = {}, page = 1, limit = 20) => {
    try {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
            ...Object.fromEntries(
                Object.entries(filters).filter(([_, value]) => value !== undefined && value !== null && value !== '')
            )
        });

        const response = await api.get(`/admin/assinaturas?${params.toString()}`);
        return response.data;
    } catch (error) {
        console.error('[AdminAssinaturaAPI] Erro ao listar assinaturas:', error);
        throw error;
    }
};

/**
 * Busca detalhes de uma organização específica
 * @param {number} organizacaoId - ID da organização
 * @returns {Promise} Detalhes da organização e assinaturas
 */
export const buscarDetalhesOrganizacao = async (organizacaoId) => {
    try {
        const response = await api.get(`/admin/assinaturas/${organizacaoId}`);
        return response.data;
    } catch (error) {
        console.error('[AdminAssinaturaAPI] Erro ao buscar detalhes:', error);
        throw error;
    }
};

/**
 * Atualiza o plano de uma organização
 * @param {number} organizacaoId - ID da organização
 * @param {number} planoId - ID do novo plano
 * @param {string} observacoes - Observações opcionais
 * @returns {Promise} Resultado da atualização
 */
export const atualizarPlano = async (organizacaoId, planoId, observacoes = null) => {
    try {
        const response = await api.put(`/admin/assinaturas/${organizacaoId}/plano`, {
            plano_id: planoId,
            observacoes
        });
        return response.data;
    } catch (error) {
        console.error('[AdminAssinaturaAPI] Erro ao atualizar plano:', error);
        throw error;
    }
};

/**
 * Bloqueia uma organização
 * @param {number} organizacaoId - ID da organização
 * @param {string} observacoes - Observações opcionais
 * @returns {Promise} Resultado do bloqueio
 */
export const bloquearOrganizacao = async (organizacaoId, observacoes = null) => {
    try {
        const response = await api.post(`/admin/assinaturas/${organizacaoId}/bloquear`, {
            observacoes
        });
        return response.data;
    } catch (error) {
        console.error('[AdminAssinaturaAPI] Erro ao bloquear organização:', error);
        throw error;
    }
};

/**
 * Desbloqueia uma organização
 * @param {number} organizacaoId - ID da organização
 * @param {string} observacoes - Observações opcionais
 * @returns {Promise} Resultado do desbloqueio
 */
export const desbloquearOrganizacao = async (organizacaoId, observacoes = null) => {
    try {
        const response = await api.post(`/admin/assinaturas/${organizacaoId}/desbloquear`, {
            observacoes
        });
        return response.data;
    } catch (error) {
        console.error('[AdminAssinaturaAPI] Erro ao desbloquear organização:', error);
        throw error;
    }
};

/**
 * Renova assinatura de uma organização
 * @param {number} organizacaoId - ID da organização
 * @param {number} diasAdicionais - Dias a adicionar (padrão: 365)
 * @param {string} observacoes - Observações opcionais
 * @returns {Promise} Resultado da renovação
 */
export const renovarAssinatura = async (organizacaoId, diasAdicionais = 365, observacoes = null) => {
    try {
        const response = await api.post(`/admin/assinaturas/${organizacaoId}/renovar`, {
            dias_adicionais: diasAdicionais,
            observacoes
        });
        return response.data;
    } catch (error) {
        console.error('[AdminAssinaturaAPI] Erro ao renovar assinatura:', error);
        throw error;
    }
};

/**
 * Busca estatísticas de assinaturas
 * @returns {Promise} Estatísticas gerais
 */
export const obterEstatisticas = async () => {
    try {
        const response = await api.get('/admin/assinaturas/estatisticas');
        return response.data;
    } catch (error) {
        console.error('[AdminAssinaturaAPI] Erro ao obter estatísticas:', error);
        throw error;
    }
};
